import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import Copy01Icon from "@redotech/redo-icons/copy-01.svg";
import { CHARGEBACKS_SHOPIFY_APP_URL } from "@redotech/redo-referrals-attribution/app-url";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  type BreadcrumbSlot,
  useBreadcrumb,
} from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { ImageUpload } from "@redotech/redo-web/image-upload";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { ReferrerDetail } from "@redotech/admin-sdk/rpc/schema/referrers/shared";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { ClientUploader } from "../client/uploader";
import { DetailPageBackButton } from "../onboardings/components/DetailPageBackButton";
// Reuse the merchant detail page's layout CSS so the referrer detail
// surface reads as the same kind of page (sticky white header with
// title + tags row, in-header tab strip). Referrers only have one level
// of navigation, so the tab strip lives in the white header — not a grey
// bar below.
import * as shellStyles from "../onboardings/merchants/merchant-detail-page.module.css";
import { ImpersonateReferrerButton } from "./components/impersonate-referrer-button";
import { ReferrerActionPanel } from "./components/referrer-action-panel";
import { ReferrerAuthEventsList } from "./components/referrer-auth-events-list";
import { ReferrerInstallsList } from "./components/referrer-installs-list";
import { ReferrerStatusBadge } from "./components/referrer-status-badge";
import { mapReferrerErrorToFieldError } from "./lib/map-referrer-error-to-field-errors";

type TabKey = "overview" | "auth-events" | "installs" | "payouts";
const VALID_TABS: TabKey[] = ["overview", "auth-events", "installs", "payouts"];

interface ReferrerDetailPageProps {
  breadcrumb: BreadcrumbSlot;
}

export const ReferrerDetailPage = memo(function ReferrerDetailPage({
  breadcrumb,
}: ReferrerDetailPageProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const navigate = useNavigate();
  const { referrerId } = useParams<{ referrerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey = parseTab(searchParams.get("tab"));
  const setTab = useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(searchParams);
      if (next === "overview") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const detailLoad = useLoad(
    async (signal) => {
      if (!referrerId) {
        return null;
      }
      return rpcClient.getReferrer({ referrerId }, { signal });
    },
    [rpcClient, referrerId, refreshKey],
  );

  const handleBack = useCallback(() => navigate("/referrers"), [navigate]);

  useBreadcrumb(breadcrumb, detailLoad.value?.referrer?.name || "");

  if (detailLoad.pending && detailLoad.value == null) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.loadingContainer}>Loading referrer…</div>
      </div>
    );
  }
  if (detailLoad.error) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.errorContainer}>
          <Text fontSize="md" textColor="error">
            Failed to load referrer.
          </Text>
          <RedoButton
            hierarchy="secondary"
            onClick={refresh}
            size="sm"
            text="Retry"
          />
        </div>
      </div>
    );
  }
  const referrer = detailLoad.value?.referrer;
  if (!referrer) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.errorContainer}>Referrer not found.</div>
      </div>
    );
  }

  const isInstallsTab = tab === "installs";

  return (
    <Flex
      className={shellStyles.pageWrapper}
      dir="column"
      gap="none"
      height={isInstallsTab ? "screen" : undefined}
      overflow={isInstallsTab ? "hidden" : undefined}
    >
      <div className={shellStyles.topHeader}>
        <DetailPageBackButton onClick={handleBack} />
        <div className={shellStyles.topHeaderContent}>
          <div className={shellStyles.topHeaderMain}>
            <div className={shellStyles.topHeaderLeft}>
              <div className={shellStyles.headerTags}>
                <ReferrerStatusBadge status={referrer.status} />
              </div>
            </div>
            <div className={shellStyles.headerActions}>
              <ImpersonateReferrerButton referrer={referrer} />
              <ReferrerActionPanel
                onReferrerChanged={refresh}
                referrer={referrer}
              />
            </div>
          </div>
          <div className={shellStyles.productNavBar}>
            {VALID_TABS.map((t) => (
              <Flex
                as="button"
                className={classNames(
                  shellStyles.productTab,
                  tab === t && shellStyles.productTabActive,
                )}
                gap="xs"
                key={t}
                onClick={() => setTab(t)}
                type="button"
              >
                {TAB_LABELS[t]}
              </Flex>
            ))}
          </div>
        </div>
      </div>
      {isInstallsTab ? (
        <Flex dir="column" flex={1} gap="xl" overflow="hidden" p="2xl" w="full">
          <ReferrerInstallsList
            installCount={referrer.installCount}
            referrerId={referrer.id}
          />
        </Flex>
      ) : (
        <div className={shellStyles.pageContainer}>
          {tab === "overview" && (
            <OverviewTab onReferrerChanged={refresh} referrer={referrer} />
          )}
          {tab === "auth-events" && (
            <ReferrerAuthEventsList events={referrer.recentAuthEvents} />
          )}
          {tab === "payouts" && (
            <DeferredTab description="Stripe payouts land in PR10." />
          )}
        </div>
      )}
    </Flex>
  );
});

const TAB_LABELS: Record<TabKey, string> = {
  overview: "Overview",
  "auth-events": "Auth events",
  installs: "Installs",
  payouts: "Payouts",
};

function parseTab(raw: string | null): TabKey {
  if (raw && (VALID_TABS as string[]).includes(raw)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return raw as TabKey;
  }
  return "overview";
}

interface EditableNameRowProps {
  referrer: ReferrerDetail;
  onUpdated: () => void;
}

const EditableNameRow = memo(function EditableNameRow({
  referrer,
  onUpdated,
}: EditableNameRowProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(referrer.name);
  const [submitting, setSubmitting] = useState(false);

  const cancel = useCallback(() => {
    setDraft(referrer.name);
    setEditing(false);
  }, [referrer.name]);

  const save = useCallback(async () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      toast("Name cannot be blank.", { variant: "error" });
      return;
    }
    if (trimmed === referrer.name) {
      setEditing(false);
      return;
    }
    setSubmitting(true);
    try {
      await rpcClient.updateReferrer({
        referrerId: referrer.id,
        name: trimmed,
      });
      toast("Name updated.", { variant: "success" });
      setEditing(false);
      onUpdated();
    } catch (err) {
      const mapping = mapReferrerErrorToFieldError(err);
      toast(
        mapping?.message ??
          (err instanceof Error ? err.message : "Failed to update name."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  }, [draft, referrer.id, referrer.name, rpcClient, onUpdated]);

  return (
    <Flex align="center" dir="row" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        Name
      </Text>
      {editing ? (
        <>
          <RedoTextInput
            placeholder="Referrer name"
            setValue={setDraft}
            value={draft}
          />
          <RedoButton
            disabled={submitting}
            hierarchy="primary"
            onClick={() => void save()}
            size="xs"
            text={submitting ? "Saving…" : "Save"}
          />
          <RedoButton
            disabled={submitting}
            hierarchy="tertiary"
            onClick={cancel}
            size="xs"
            text="Cancel"
          />
        </>
      ) : (
        <>
          <Text fontSize="sm">{referrer.name}</Text>
          <RedoButton
            hierarchy="tertiary"
            onClick={() => setEditing(true)}
            size="xs"
            text="Edit"
          />
        </>
      )}
    </Flex>
  );
});

interface OverviewTabProps {
  referrer: ReferrerDetail;
  onReferrerChanged: () => void;
}

const OverviewTab = memo(function OverviewTab({
  referrer,
  onReferrerChanged,
}: OverviewTabProps) {
  const copyAppLink = useCallback(() => {
    void navigator.clipboard.writeText(CHARGEBACKS_SHOPIFY_APP_URL).then(
      () => {
        toast("Listing link copied.", { variant: "success" });
      },
      () => {
        toast("Couldn't copy listing link.", { variant: "error" });
      },
    );
  }, []);

  return (
    <div className={shellStyles.tabContent}>
      <div className={shellStyles.metaSection}>
        <EditableNameRow onUpdated={onReferrerChanged} referrer={referrer} />
        <ReferrerProfileImageSection
          key={`${referrer.id}-${referrer.updatedAt}-${referrer.profileImageUrl ?? ""}`}
          onUpdated={onReferrerChanged}
          referrer={referrer}
        />
        <MetadataRow label="Referrer ID" value={referrer.id} />
        <MetadataRow label="Email" value={referrer.email} />
        <MetadataRow
          label="Referrer type"
          value={referrerTypeLabel(referrer.referrerType)}
        />
        <MetadataRow label="Referral code" value={referrer.referralCode} />
        <MetadataRow
          label="Attributed installs"
          value={referrer.installCount.toLocaleString()}
        />
        <Flex align="center" dir="row" gap="md">
          <Text fontSize="sm" fontWeight="semibold">
            Chargebacks listing
          </Text>
          <Text fontSize="sm">{CHARGEBACKS_SHOPIFY_APP_URL}</Text>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={Copy01Icon}
            onClick={copyAppLink}
            size="xs"
            text="Copy"
          />
        </Flex>
        <Text fontSize="sm" textColor="tertiary">
          Referral credit will come from merchant code entry during onboarding.
        </Text>
        <MetadataRow label="Created by" value={referrer.createdBy} />
        <MetadataRow
          label="Created"
          value={new Date(referrer.createdAt).toLocaleString()}
        />
        <MetadataRow
          label="Last updated"
          value={new Date(referrer.updatedAt).toLocaleString()}
        />
      </div>
    </div>
  );
});

interface ReferrerProfileImageSectionProps {
  referrer: ReferrerDetail;
  onUpdated: () => void;
}

const ReferrerProfileImageSection = memo(function ReferrerProfileImageSection({
  referrer,
  onUpdated,
}: ReferrerProfileImageSectionProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [profileImageUrl, setProfileImageUrl] = useState(
    referrer.profileImageUrl,
  );
  const [submitting, setSubmitting] = useState(false);

  const save = useCallback(
    async (nextValue: URL | undefined) => {
      const previousValue = profileImageUrl;
      const nextProfileImageUrl = nextValue?.toString() ?? null;
      setProfileImageUrl(nextProfileImageUrl);
      setSubmitting(true);
      try {
        await rpcClient.updateReferrer({
          referrerId: referrer.id,
          profileImageUrl: nextProfileImageUrl,
        });
        toast(
          nextProfileImageUrl
            ? "Profile image updated."
            : "Profile image removed.",
          { variant: "success" },
        );
        onUpdated();
      } catch (err) {
        setProfileImageUrl(previousValue);
        toast(err instanceof Error ? err.message : "Failed to update image.", {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [onUpdated, referrer.id, profileImageUrl, rpcClient],
  );

  return (
    <Flex align="flex-start" dir="column" gap="md">
      <Flex dir="column" gap="xs" maxW="sm">
        <Text fontSize="md" fontWeight="semibold">
          Referrer profile picture
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Used for this referrer account in admin and the referrals portal.
        </Text>
      </Flex>
      <ClientUploader>
        <Flex grow={0} height="fit-content" w="xs">
          <ImageUpload
            accept={["image/png", "image/jpeg", "image/webp", "image/gif"]}
            caption="PNG, JPG, WebP, or GIF."
            disabled={submitting}
            onChange={(nextValue) => void save(nextValue)}
            showErrorMessages
            value={toOptionalUrl(profileImageUrl)}
          />
        </Flex>
      </ClientUploader>
    </Flex>
  );
});

function toOptionalUrl(value: string | null): URL | undefined {
  if (!value) {
    return undefined;
  }
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex align="center" dir="row" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        {label}
      </Text>
      <Text fontSize="sm">{value}</Text>
    </Flex>
  );
}

function referrerTypeLabel(type: string): string {
  switch (type) {
    case "individual":
      return "Individual";
    case "app":
      return "App";
    case "agency":
      return "Agency";
    default:
      return type;
  }
}

function DeferredTab({ description }: { description: string }) {
  return (
    <Flex align="center" justify="center" p="3xl">
      <Text fontSize="md" textColor="secondary">
        {description}
      </Text>
    </Flex>
  );
}
